import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { apiClient } from '@/lib/api-client';
import type { LocationTaxObject, TerminalPrro } from '@/hooks/use-fiscal-registration';

/**
 * PFC-19 — the DPS object tree: what the tax service holds for the taxpayer of
 * a signing key (its main tax office, taxation objects and PRROs), read with
 * the key's unlocked session and shown next to our records.
 *
 * A read writes nothing. The server keeps it for 30 minutes under its
 * `snapshotId`; each copy is a separate, explicit action on one item of that
 * read and answers the refreshed tree, which goes straight into the cache.
 * Dates DPS gives are `YYYY-MM-DD`.
 */

export type DpsRegistrationStatus = 'registered' | 'deregistered';

/**
 * `exact`: DPS used the form's wording, so the code is DPS's and a copy keeps
 * it. `stem`: our reading of DPS's words, which the operator may correct.
 * `none`: not recognised; the operator gives the code.
 */
export type CodeMatch = 'exact' | 'stem' | 'none';

export type ObjectTreeDpsObject = {
  toCode: string;
  objectTypeCode: number;
  typeName: string | null;
  name: string | null;
  address: string | null;
  state: string | null;
  stateCode: number | null;
  /** How `stateCode` was read: DPS's own form wording, a word stem, or not at all. */
  stateMatch: CodeMatch;
  rights: string | null;
  rightsCode: number | null;
  rightsMatch: CodeMatch;
  registeredAt: string | null;
  deregisteredAt: string | null;
  katottg: string | null;
  taxOffice: string | null;
  changedAt: string | null;
};

export type ObjectTreeObject = {
  dps: ObjectTreeDpsObject;
  objectTypeName: string | null;
  settlement: string | null;
  /** The fields a copy writes from DPS. */
  proposed: {
    dpsObjectCode: string;
    objectTypeCode: number;
    katottg: string | null;
    stateCode: number | null;
    rightsCode: number | null;
    dpsRegistrationStatus: DpsRegistrationStatus;
    dpsRegisteredAt: string | null;
  };
  match: {
    location: { id: string; code: string; name: string };
    taxObject: LocationTaxObject;
  } | null;
};

export type ObjectTreePrro = {
  dps: {
    fiscalNumber: string;
    localNumber: string | null;
    name: string | null;
    status: string | null;
    registrationStatus: DpsRegistrationStatus | null;
    registeredAt: string | null;
    address: string | null;
  };
  proposed: {
    fiscalNumber: string;
    localNumber: string | null;
    prroName: string | null;
    registrationStatus: DpsRegistrationStatus | null;
    registeredAt: string | null;
  };
  match: {
    terminal: { id: string; code: string; displayName: string };
    location: { id: string; code: string; name: string } | null;
    prro: TerminalPrro;
  } | null;
};

export type ObjectTree = {
  snapshotId: string;
  keyId: string;
  readAt: string;
  expiresAt: string;
  payer: { tin: string; name: string | null };
  legalEntity: {
    id: string;
    kind: 'fop' | 'llc';
    tin: string;
    name: string;
    taxOfficeCode: string;
    taxOfficeName: string;
    dpsSyncedAt: string | null;
  };
  registration: {
    taxOfficeCode: string | null;
    taxOfficeName: string | null;
    registeredAt: string | null;
    /** Whether our office directory knows DPS's office (a copy needs it to). */
    taxOfficeKnown: boolean;
    same: boolean;
  };
  objects: ObjectTreeObject[];
  prros: ObjectTreePrro[];
};

export type CopyTaxObjectBody = {
  locationId: string;
  stateCode?: number;
  rightsCode?: number;
  katottg?: string;
  address?: { settlement: string };
  objectName?: string | null;
  registerAtNonMainPlace?: boolean;
  stateRegistrationNumber?: string | null;
};

export type CopyPrroBody = {
  terminalId: string;
  /** Used only when DPS gives none. */
  localNumber?: string;
  prroName?: string;
  prroTypeCode?: string;
  offlineModeCode?: string;
};

export type ObjectTreeIssue = { path: string; message: string; code?: string };

/** A read's id as the server issues it; anything else never reaches a request path. */
export const SNAPSHOT_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The reference-data check's refusal: a 400 whose issues carry this code. */
export const UNKNOWN_REFERENCE_CODE = 'unknown_reference_code';

/** The API's refusal: its stable code and, for an incomplete copy, the fields it lacks. */
export function objectTreeFailure(err: unknown): {
  code: string | null;
  issues: ObjectTreeIssue[];
} {
  if (!isAxiosError(err)) return { code: null, issues: [] };
  const body = err.response?.data as { code?: unknown; issues?: unknown } | undefined;
  const raw: unknown = body?.issues;
  const issues = Array.isArray(raw)
    ? (raw as unknown[]).flatMap((issue): ObjectTreeIssue[] => {
        if (typeof issue !== 'object' || issue === null) return [];
        const { path, message, code } = issue as {
          path?: unknown;
          message?: unknown;
          code?: unknown;
        };
        return typeof path === 'string' && typeof message === 'string'
          ? [{ path, message, ...(typeof code === 'string' ? { code } : {}) }]
          : [];
      })
    : [];
  if (typeof body?.code === 'string') return { code: body.code, issues };
  // The reference-data check names its refusal per issue, not at the top.
  const unknownCode = issues.some((issue) => issue.code === UNKNOWN_REFERENCE_CODE);
  return { code: unknownCode ? UNKNOWN_REFERENCE_CODE : null, issues };
}

/**
 * What an operator reads for each code a read or a copy may answer. Signer
 * codes (a revoked certificate, a signing timeout) fall back to the signing
 * keys' own messages; anything else to a generic one.
 */
export const OBJECT_TREE_ERROR_KEY: Record<string, string> = {
  KEY_SESSION_LOCKED: 'fiscalCabinet.objectTree.error.KEY_SESSION_LOCKED',
  FISCAL_SIGNING_KEY_NOT_CURRENT: 'fiscalCabinet.objectTree.error.FISCAL_SIGNING_KEY_NOT_CURRENT',
  DPS_CABINET_REFUSED: 'fiscalCabinet.objectTree.error.DPS_CABINET_REFUSED',
  DPS_CABINET_UNAVAILABLE: 'fiscalCabinet.objectTree.error.DPS_CABINET_UNAVAILABLE',
  DPS_CABINET_BAD_REPLY: 'fiscalCabinet.objectTree.error.DPS_CABINET_BAD_REPLY',
  DPS_PAYER_MISMATCH: 'fiscalCabinet.objectTree.error.DPS_PAYER_MISMATCH',
  DPS_PAYER_CHANGED: 'fiscalCabinet.objectTree.error.DPS_PAYER_CHANGED',
  DPS_READ_EXPIRED: 'fiscalCabinet.objectTree.error.DPS_READ_EXPIRED',
  DPS_VALUE_MISSING: 'fiscalCabinet.objectTree.error.DPS_VALUE_MISSING',
  DPS_COPY_INCOMPLETE: 'fiscalCabinet.objectTree.error.DPS_COPY_INCOMPLETE',
  FISCAL_OBJECT_CODE_TAKEN: 'fiscalCabinet.objectTree.error.FISCAL_OBJECT_CODE_TAKEN',
  FISCAL_LEGAL_ENTITY_INACTIVE: 'fiscalCabinet.objectTree.error.FISCAL_LEGAL_ENTITY_INACTIVE',
  DPS_PRRO_OTHER_TAXPAYER: 'fiscalCabinet.objectTree.error.DPS_PRRO_OTHER_TAXPAYER',
  DPS_PRRO_REVOKED_TERMINAL: 'fiscalCabinet.objectTree.error.DPS_PRRO_REVOKED_TERMINAL',
  FISCAL_PRRO_NUMBER_TAKEN: 'fiscalCabinet.objectTree.error.FISCAL_PRRO_NUMBER_TAKEN',
  DPS_OBJECT_OTHER_TAXPAYER: 'fiscalCabinet.objectTree.error.DPS_OBJECT_OTHER_TAXPAYER',
  FISCAL_OBJECT_NUMBERS_EXHAUSTED: 'fiscalCabinet.objectTree.error.FISCAL_OBJECT_NUMBERS_EXHAUSTED',
  FISCAL_LEGAL_ENTITY_NOT_FOUND: 'fiscalCabinet.objectTree.error.FISCAL_LEGAL_ENTITY_NOT_FOUND',
  unknown_reference_code: 'fiscalCabinet.objectTree.error.unknown_reference_code',
};
export const OBJECT_TREE_ERROR: Record<string, string> = {
  KEY_SESSION_LOCKED: 'The key is locked. Unlock it on the Signing keys tab, then read again.',
  FISCAL_SIGNING_KEY_NOT_CURRENT:
    'This key has been revoked or replaced. Choose the current key of the legal entity.',
  DPS_CABINET_REFUSED: 'The tax service refused this key for this taxpayer.',
  DPS_CABINET_UNAVAILABLE: 'The tax service is not answering right now. Try again later.',
  DPS_CABINET_BAD_REPLY: 'The reply from the tax service could not be read. Try again later.',
  DPS_PAYER_MISMATCH:
    'The tax service answered for another taxpayer than the legal entity of this key.',
  DPS_PAYER_CHANGED:
    'The legal entity has changed taxpayer since this read. Read from the tax service again.',
  DPS_READ_EXPIRED: 'The read has expired; read again.',
  DPS_VALUE_MISSING: 'The tax service gave no value to copy.',
  DPS_COPY_INCOMPLETE:
    'The record would not be complete. Fill in what the tax service does not give:',
  FISCAL_OBJECT_CODE_TAKEN: 'Another store already has this taxation object code.',
  FISCAL_LEGAL_ENTITY_INACTIVE: 'The legal entity is retired.',
  DPS_PRRO_OTHER_TAXPAYER:
    "This till's store is registered for another legal entity, so this PRRO cannot be copied to it.",
  DPS_PRRO_REVOKED_TERMINAL: 'This till has been revoked, so no PRRO can be copied to it.',
  FISCAL_PRRO_NUMBER_TAKEN: 'Another till already has this fiscal number.',
  DPS_OBJECT_OTHER_TAXPAYER:
    'This store is registered for another legal entity. A copy does not move a store to another taxpayer; change its legal entity on the store first.',
  FISCAL_OBJECT_NUMBERS_EXHAUSTED: 'This legal entity has no free object number left.',
  FISCAL_LEGAL_ENTITY_NOT_FOUND: 'The legal entity no longer exists.',
  unknown_reference_code:
    'A code is not in the current reference data of the tax service. Check it:',
};

const ROOT = ['fiscal-cabinet', 'object-tree'] as const;
const readKey = (snapshotId: string) => [...ROOT, 'read', snapshotId] as const;
const readPath = (snapshotId: string) =>
  `/fiscal-cabinet/object-tree/reads/${encodeURIComponent(snapshotId)}`;

/**
 * Whether this server can sign at all. On the stub signer no key can be
 * unlocked and every read is refused, so the page says so up front.
 */
export function useObjectTreeSigner(enabled: boolean) {
  return useQuery({
    queryKey: [...ROOT, 'signer'] as const,
    enabled,
    staleTime: Infinity,
    queryFn: async (): Promise<{ signingConfigured: boolean }> =>
      (await apiClient.get<{ signingConfigured: boolean }>('/fiscal-cabinet/object-tree/signer'))
        .data,
  });
}

/** Read the key's taxpayer from DPS; the answer is cached under its snapshot. */
export function useReadObjectTree() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (keyId: string) =>
      (await apiClient.post<ObjectTree>('/fiscal-cabinet/object-tree/read', { keyId })).data,
    onSuccess: (tree) => {
      qc.setQueryData(readKey(tree.snapshotId), tree);
      // The read signed with the key: the signature log has a new row.
      void qc.invalidateQueries({ queryKey: ['fiscal-cabinet', 'signature-audit'] });
    },
  });
}

/** A read kept on the server; answers `DPS_READ_EXPIRED` (404) once it is gone. */
export function useObjectTreeRead(snapshotId: string | null) {
  return useQuery({
    queryKey: readKey(snapshotId ?? ''),
    enabled: snapshotId !== null && SNAPSHOT_ID_PATTERN.test(snapshotId),
    staleTime: 60_000,
    // An expired read stays expired; asking again only delays saying so.
    retry: (count, err) => objectTreeFailure(err).code !== 'DPS_READ_EXPIRED' && count < 2,
    queryFn: async (): Promise<ObjectTree> =>
      (await apiClient.get<ObjectTree>(readPath(snapshotId ?? ''))).data,
  });
}

/** After a copy: the tree as answered, and every page showing our registration data. */
function afterCopy(qc: QueryClient, tree: ObjectTree): void {
  qc.setQueryData(readKey(tree.snapshotId), tree);
  void qc.invalidateQueries({ queryKey: ['fiscal-cabinet', 'registration'] });
  void qc.invalidateQueries({ queryKey: ['fiscal-cabinet', 'journal', 'registers'] });
}

export function useCopyTaxOffice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (snapshotId: string) =>
      (await apiClient.post<ObjectTree>(`${readPath(snapshotId)}/tax-office`)).data,
    onSuccess: (tree) => afterCopy(qc, tree),
  });
}

export function useCopyTaxObject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      snapshotId,
      toCode,
      body,
    }: {
      snapshotId: string;
      toCode: string;
      body: CopyTaxObjectBody;
    }) =>
      (
        await apiClient.post<ObjectTree>(
          `${readPath(snapshotId)}/objects/${encodeURIComponent(toCode)}`,
          body,
        )
      ).data,
    onSuccess: (tree) => afterCopy(qc, tree),
  });
}

export function useCopyPrro() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      snapshotId,
      fiscalNumber,
      body,
    }: {
      snapshotId: string;
      fiscalNumber: string;
      body: CopyPrroBody;
    }) =>
      (
        await apiClient.post<ObjectTree>(
          `${readPath(snapshotId)}/prros/${encodeURIComponent(fiscalNumber)}`,
          body,
        )
      ).data,
    onSuccess: (tree) => afterCopy(qc, tree),
  });
}
